import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL = "/api/static/assets/8b8de18e_1msnypyd_GLS_LOGO_2_.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/ebooks", label: "Ebooks" },
  { to: "/courses", label: "Courses" },
  { to: "/faculty", label: "Faculty" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/media", label: "Media" },
  { to: "/blog", label: "Blog" },
  { to: "/faqs", label: "FAQs" },
  { to: "/newsletter", label: "Newsletter" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const { count } = useCart();
  const { user } = useAuth() || {};
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header data-testid="site-header" className="bg-white border-b border-[#E8DCDD] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link to="/" data-testid="header-logo-link" className="flex items-center flex-shrink-0 gap-3">
            <img src={LOGO_URL} alt="Global Leader Solutions" data-testid="header-brand-logo" className="h-14 w-14 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-2xl sm:text-3xl text-[#8C4A57] tracking-tight">Global Leader</span>
              <span className="font-sans text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#6B5E62] mt-1">Solutions</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" data-testid="primary-nav">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-sm tracking-wide transition-colors duration-200 hover:text-[#8C4A57] ${
                  pathname === l.to ? "text-[#8C4A57] font-semibold" : "text-[#2D2628]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to={user ? "/dashboard" : "/login"}
              data-testid={user ? "header-dashboard-link" : "header-login-link"}
              className="hidden sm:inline-block text-[10px] tracking-[0.14em] uppercase font-semibold text-[#8C4A57] border border-[#E8DCDD] rounded-sm px-3 py-2 hover:border-[#8C4A57] transition"
            >
              {user ? "My Library" : "Sign In"}
            </Link>
            <Link
              to="/cart"
              data-testid="header-cart-link"
              className="relative flex items-center justify-center w-10 h-10 border border-[#E8DCDD] rounded-sm hover:border-[#8C4A57] transition-colors duration-200"
            >
              <ShoppingBag className="w-4 h-4 text-[#8C4A57]" />
              {count > 0 && (
                <span data-testid="cart-badge" className="absolute -top-2 -right-2 bg-[#8C4A57] text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center border border-[#E8DCDD] rounded-sm"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-4 h-4 text-[#8C4A57]" /> : <Menu className="w-4 h-4 text-[#8C4A57]" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-[#E8DCDD] bg-white" data-testid="mobile-nav">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={`text-sm py-2 ${pathname === l.to ? "text-[#8C4A57] font-semibold" : "text-[#2D2628]"}`}>
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
